import { inject, Injectable } from '@angular/core';
import { SearcherState } from '@vueling-ng/vy-cdk-searcher';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { AnalyticsDigitalService } from './analytics-digital.service';
import {
  DataLayerEntry,
  dataLayerName,
  digitalDataName,
  EventFields,
  GaEvents,
  PageViewEventFields
} from './models/analytics.config';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsDataService {
  private readonly _analyticsDigitalService = inject(AnalyticsDigitalService);
  private readonly _pushDigitalDataActions$: Subject<unknown> = new Subject<unknown>();

  public startGoogleAnalytics(): void {
    if (environment.analytics.key && environment.analytics.activeDigitalData) {
      this._pushDigitalDataActions$.subscribe((digitalData: unknown) => {
        this.pushDataLayer(digitalData);
        this.setToDigitalData(digitalData);
      });

      this.install();
    }
  }

  public trackEvent(event: GaEvents, value: EventFields): void {
    let eventData;
    switch (event) {
      case 'page_view':
        if (this.isPageViewEventFields(value)) {
          eventData = this._analyticsDigitalService.buildPageTrackingData(value.route, value.filter);
        }
        break;
      case 'search_start':
        if (this.isSearcherState(value)) {
          eventData = this._analyticsDigitalService.buildSearcherTrackingData(value);
        }
        break;
    }
    this._pushDigitalDataActions$.next(eventData);
  }

  private isSearcherState(value: EventFields): value is SearcherState {
    return 'flightType' in value;
  }

  private isPageViewEventFields(value: EventFields): value is PageViewEventFields {
    return 'route' in value;
  }

  private install(): void {
    if (!window[digitalDataName]) {
      window[digitalDataName] = {};
    }

    if (!window[dataLayerName]) {
      window[dataLayerName] = [];
      const scriptChildInitialization = document.createElement('script');
      scriptChildInitialization.text = '';

      const scriptChildInstallation = document.createElement('script');
      scriptChildInstallation.text = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${environment.analytics.key}');
        `.trim();
      document.head.appendChild(scriptChildInstallation);

      const noScriptChild = document.createElement('noscript');
      noScriptChild.textContent =
        '<iframe src="https://www.googletagmanager.com/ns.html?id=' +
        environment.analytics.key +
        '"\n' +
        'height="0" width="0" style="display:none;visibility:hidden"></iframe>';
      document.body.prepend(noScriptChild);
    }
  }

  private pushDataLayer(digitalData: unknown): void {
    this.trace(digitalData);
    this.getDataLayer().push(this.sanitizeData(digitalData) as DataLayerEntry);
  }

  private getDataLayer(): DataLayerEntry[] {
    return (window[dataLayerName] as DataLayerEntry[]) || [];
  }

  private setToDigitalData(info: unknown): void {
    window[digitalDataName] = {
      ...(window[digitalDataName] as Record<string, unknown>),
      ...(info as Record<string, unknown>)
    };
  }

  private trace(digitalData: unknown): void {
    if (environment.analytics.enableTracing) {
      console.groupCollapsed('analytics trace');
      console.groupCollapsed('send digital data');
      console.info(JSON.stringify(digitalData, null, 2));
      console.groupEnd();
      console.groupEnd();
    }
  }

  private sanitizeData(data: unknown): unknown {
    return JSON.parse(JSON.stringify(data));
  }
}
